/*
 * ==========================================================================
 * SingRank - CTA click capture for RCS                          v1.0.0
 * renovationcontractorsingapore.com
 * --------------------------------------------------------------------------
 * WHAT THIS ADDS, AND WHY IT IS SAFE TO ADD
 *
 * RCS already records two things through the tracker in layout/theme.liquid:
 *   - WhatsApp clicks   367 rows as at 2026-09-01
 *   - contact form submits  67 rows
 *
 * It records NOTHING for the "Ask Aideal lah!" buttons. Measured 2026-09-01:
 * the string "ai-deal" appears ZERO times in the entire leads table, and
 * ai-deal.ai is not served from our NAS, so there are no server logs to fall
 * back on either. Those clicks have never been counted anywhere - and there
 * are a lot of them: 2 on the homepage, 6 on a typical article, 2 on the
 * contact page.
 *
 * So this file captures ONLY the three things nothing else is listening for:
 *
 *   1. Outbound CTA clicks   any link to ai-deal.ai / aideal.ai
 *   2. Phone taps            tel: links
 *   3. Email taps            mailto: links
 *
 * It does NOT touch WhatsApp and does NOT touch form submits. The existing
 * tracker owns both, and listening twice writes two rows per action - the
 * exact fault found on RCS on 2026-09-01, where one client was landing in two
 * buckets and every AI visit was counted twice. Purely additive: nothing that
 * works today changes.
 *
 * SLUG IS HARD-CODED "rcs"
 * Do not derive it from location.hostname here. That would send
 * "renovationcontractorsingapore.com" and open a second bucket beside the 440
 * rows already sitting under "rcs".
 *
 * WHAT EACH ROW TELLS YOU
 *   message   Outbound click: ai-deal.ai - Ask Aideal lah!   (the button label)
 *   website   the destination URL
 *   page      the page the click happened on
 *   first_page  the page that BROUGHT the visitor to the site
 *
 * That last one is the point: RCS's top "lead page" looks like /pages/contact-us
 * with 86, but the contact page brought nobody - an article did. first_page is
 * what lets an article take the credit it earned.
 *
 * CONTENT TYPE IS text/plain AND MUST STAY THAT WAY
 * application/json triggers a CORS preflight that sendBeacon cannot perform,
 * and the row is lost silently. The server reads the raw body regardless.
 *
 * SAFETY
 * - Never blocks, delays or rewrites a click. Fire and forget.
 * - One row per click: a 1.2s same-target debounce absorbs the duplicate
 *   events that fire when a button sits inside another clickable element.
 * - Guarded by window.__SR_CTA_CLICKS__ so a second copy is a no-op.
 * - try/catch throughout; on failure the page behaves exactly as before.
 * - No regular expressions and no escape sequences, by house rule.
 *
 * DEBUG (in the browser console on the live site)
 *   window.SR_CTA.state()   slug, first-touch, last payload sent
 *   window.SR_CTA.test()    send a TEST-DELETE row, for install verification
 *
 * Maintained by SingRank. Created 2026-09-01.
 * ==========================================================================
 */
(function () {
  'use strict';

  if (window.__SR_CTA_CLICKS__) { return; }
  window.__SR_CTA_CLICKS__ = true;

  var ENDPOINT = 'https://leads.rumah.live/track';
  var SLUG = 'rcs';

  var OUTBOUND_HOSTS = ['ai-deal.ai', 'aideal.ai'];
  var WHATSAPP_MARKERS = ['wa.me', 'whatsapp.com', 'whatsapp:'];

  var STATE = { slug: SLUG, first: null, gclid: '', lastSent: null, count: 0 };
  var lastClick = { key: '', at: 0 };

  function low(s) { return String(s == null ? '' : s).toLowerCase(); }

  function contains(haystack, needles) {
    var h = low(haystack);
    if (!h) { return false; }
    for (var i = 0; i < needles.length; i++) {
      if (h.indexOf(needles[i]) > -1) { return true; }
    }
    return false;
  }

  function param(name) {
    try {
      var q = window.location.search || '';
      if (!q) { return ''; }
      var parts = q.substring(1).split('&');
      for (var i = 0; i < parts.length; i++) {
        var eq = parts[i].indexOf('=');
        if (eq < 0) { continue; }
        if (decodeURIComponent(parts[i].substring(0, eq)).toLowerCase() === name) {
          return decodeURIComponent(parts[i].substring(eq + 1).split('+').join(' '));
        }
      }
    } catch (e) {}
    return '';
  }

  // Reuse whatever first-touch record the existing tracker already keeps, so
  // both halves of the site agree on where the visitor came from.
  function firstTouch() {
    var keys = ['__sr_ft_rcs', '__sr_ft_renovationcontractorsingapore.com', 'abl_ft'];
    for (var i = 0; i < keys.length; i++) {
      try {
        var raw = window.localStorage.getItem(keys[i]);
        if (!raw) { continue; }
        var o = JSON.parse(raw);
        if (o && o.p) { return o; }
        if (o && o.landing_url) {
          return { p: o.landing_url, r: o.landing_referrer || '', t: o.ts || '' };
        }
      } catch (e) {}
    }
    try {
      var fresh = {
        p: window.location.href,
        r: document.referrer || '',
        t: new Date().toISOString()
      };
      window.localStorage.setItem('__sr_ft_rcs', JSON.stringify(fresh));
      return fresh;
    } catch (e) {}
    return { p: window.location.href, r: document.referrer || '', t: '' };
  }

  function adClickId() {
    try {
      var key = '__sr_g_rcs';
      var found = param('gclid') || param('wbraid') || param('gbraid');
      if (found) { window.localStorage.setItem(key, found); return found; }
      return window.localStorage.getItem(key) || '';
    } catch (e) {}
    return param('gclid') || '';
  }

  STATE.first = firstTouch();
  STATE.gclid = adClickId();

  function send(payload) {
    try {
      payload.client   = SLUG;
      payload.page     = window.location.href;
      payload.title    = document.title || '';
      payload.referrer = document.referrer || '';
      payload.ts       = new Date().toISOString();

      if (STATE.gclid) { payload.gclid = STATE.gclid; }
      if (STATE.first && STATE.first.p) {
        payload.first_page     = STATE.first.p;
        payload.first_referrer = STATE.first.r || '';
        payload.first_ts       = STATE.first.t || '';
      }

      var body = JSON.stringify(payload);
      var blob = new Blob([body], { type: 'text/plain' });

      STATE.lastSent = payload;
      STATE.count = STATE.count + 1;

      if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob)) { return true; }

      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: body,
        keepalive: true,
        mode: 'no-cors'
      });
      return true;
    } catch (e) { return false; }
  }

  function linkHost(href) {
    try { return low(new URL(href, window.location.href).hostname); }
    catch (e) { return ''; }
  }

  function once(key) {
    var now = new Date().getTime();
    if (key === lastClick.key && (now - lastClick.at) < 1200) { return false; }
    lastClick.key = key;
    lastClick.at = now;
    return true;
  }

  document.addEventListener('click', function (ev) {
    try {
      var a = (ev.target && ev.target.closest) ? ev.target.closest('a[href]') : null;
      if (!a) { return; }

      var href = a.getAttribute('href') || '';
      if (!href) { return; }
      var h = low(href);

      // Owned by the existing tracker. Never double-count.
      if (contains(h, WHATSAPP_MARKERS)) { return; }

      var label = String(a.textContent || '').split('\n').join(' ').trim().substring(0, 60);

      if (h.indexOf('tel:') === 0) {
        if (!once('tel|' + h)) { return; }
        send({ phone: href.substring(4).trim(), message: 'Phone click' });
        return;
      }

      if (h.indexOf('mailto:') === 0) {
        if (!once('mail|' + h)) { return; }
        send({ email: href.substring(7).split('?')[0].trim(), message: 'Email click' });
        return;
      }

      var target = linkHost(href);
      if (!target) { return; }
      for (var i = 0; i < OUTBOUND_HOSTS.length; i++) {
        if (target.indexOf(OUTBOUND_HOSTS[i]) > -1) {
          if (!once('out|' + h)) { return; }
          send({
            website: href.substring(0, 290),
            message: 'Outbound click: ' + OUTBOUND_HOSTS[i] + (label ? ' - ' + label : '')
          });
          return;
        }
      }
    } catch (e) {}
  }, true);

  window.SR_CTA = {
    version: '1.0.0',
    slug: SLUG,
    outbound: OUTBOUND_HOSTS,
    state: function () { return STATE; },
    test: function () {
      return send({ name: 'TEST-DELETE', message: 'TEST-DELETE' });
    }
  };
})();
